import { useId, useState, type ReactNode } from 'react';
import { toast } from 'react-toastify';

import { PageHeader, PageShell } from '~/components/layout';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmModal,
  PanelTable,
  type PanelTableColumn,
  Tabs,
  type TabItem,
} from '~/components/ui';
import { FormField, TextField, Select } from '~/components/form';
import { useUrlTab } from '~/hooks/useUrlTab';
import { apiErrorMessage, type ApiErrorInput } from '~/lib/apiError';
import {
  useDisableSslMutation,
  useEmailSetupMutation,
  useLazyDiagnoseAllQuery,
  useUpsertCnameMutation,
  type DnsDiagnosis,
} from '~/modules/query/utilities';
import { DomainMainContent } from '~/components/pages/domain/components/domainContent';

const EMAIL_METHODS = [
  'setupDomain',
  'verify',
  'setupInbound',
  'setupInRackspace',
  'get',
  'testSend',
];

const TAB_IDS = ['dns', 'email', 'ssl', 'domain'] as const;
const TABS: TabItem[] = [
  { id: 'dns', label: 'DNS' },
  { id: 'email', label: 'Email' },
  { id: 'ssl', label: 'SSL' },
  { id: 'domain', label: 'Domain setup' },
];

type UtilityValues = Record<string, string>;

interface UtilityField {
  name: string;
  label: string;
  options?: (string | { value: string; label: string })[];
  optional?: boolean;
  placeholder?: string;
}

interface UtilityCardProps {
  /** Stable id for field ids and test ids. */
  id: string;
  title: string;
  description?: string;
  danger?: boolean;
  /** Asks first: a danger `ConfirmModal` with this message and note, before `onRun` runs. */
  confirm?: { message: (values: UtilityValues) => string; dangerNote: string };
  fields: UtilityField[];
  runLabel?: string;
  onRun: (values: UtilityValues) => Promise<unknown> | unknown;
  /** Shows a successful result; without it the result is printed as JSON. */
  renderResult?: (result: unknown) => ReactNode;
}

// A card with inputs, a run button, and a result: `renderResult`, or a JSON well.
const UtilityCard = ({
  id,
  title,
  description,
  danger,
  confirm,
  fields,
  runLabel,
  onRun,
  renderResult,
}: UtilityCardProps) => {
  const uid = useId();
  // Every field starts as '' so `onRun` and `confirm.message` (built on each render) never see undefined.
  const [values, setValues] = useState<UtilityValues>(() =>
    Object.fromEntries(fields.map(({ name }) => [name, ''])),
  );
  const [result, setResult] = useState<unknown>();
  const [running, setRunning] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const run = async () => {
    setRunning(true);
    setResult(undefined);
    try {
      const res = await onRun(values);
      setResult(res ?? 'Done');
    } catch (err) {
      console.error(err);
      toast.error(
        apiErrorMessage(err as ApiErrorInput) ?? `${title} could not be completed. Try again.`,
      );
    } finally {
      setRunning(false);
    }
  };

  const ready = fields.every(({ name, optional }) => optional || values[name]?.trim());

  return (
    <Card className={danger ? 'border-error-200 dark:border-error-500/30' : undefined}>
      <CardHeader title={title} description={description} />
      <CardBody>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {fields.map(({ name, label, options, optional, placeholder }) => {
            const fieldId = `${uid}-${name}`;
            const testId = `utilities-${id}-${name}`;
            return options ? (
              <FormField key={name} id={fieldId} label={label} required={!optional}>
                <Select
                  id={fieldId}
                  testId={`${testId}-select`}
                  placeholder="Select…"
                  placeholderSelectable
                  value={values[name] ?? ''}
                  options={options.map((option) =>
                    typeof option === 'string' ? { value: option, label: option } : option,
                  )}
                  onChange={(value) => setValues({ ...values, [name]: value })}
                />
              </FormField>
            ) : (
              <TextField
                key={name}
                id={fieldId}
                testId={`${testId}-input`}
                label={label}
                required={!optional}
                value={values[name] ?? ''}
                placeholder={placeholder}
                onChange={(value) => setValues({ ...values, [name]: value })}
              />
            );
          })}
          <div className="flex items-end">
            <Button
              variant={danger ? 'danger' : 'primary'}
              disabled={running || !ready}
              loading={running}
              onClick={confirm ? () => setConfirming(true) : run}
              testId={`utilities-${id}-run-button`}
            >
              {runLabel ?? 'Run'}
            </Button>
          </div>
        </div>
        {result !== undefined && renderResult && typeof result !== 'string' && (
          <div className="mt-6" data-testid={`utilities-${id}-result`}>
            {renderResult(result)}
          </div>
        )}
        {result !== undefined && !(renderResult && typeof result !== 'string') && (
          <pre
            className="mt-6 max-h-72 overflow-auto rounded-lg bg-gray-50 p-4 font-mono text-xs text-gray-800 dark:bg-gray-950 dark:text-white/90"
            data-testid={`utilities-${id}-result`}
          >
            {typeof result === 'string' ? result : JSON.stringify(result, null, 2)}
          </pre>
        )}
      </CardBody>
      {confirm && (
        <ConfirmModal
          isOpen={confirming}
          variant="danger"
          title={`${runLabel ?? title}?`}
          message={confirm.message(values)}
          dangerNote={confirm.dangerNote}
          confirmLabel={runLabel}
          testId={`utilities-${id}-confirm`}
          onClose={() => setConfirming(false)}
          onConfirm={() => {
            setConfirming(false);
            void run();
          }}
        />
      )}
    </Card>
  );
};

// A rejected call reports its message in place of the value.
interface DnsRow {
  source: string;
  type: string;
  name: string;
  value: string;
}

const asList = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);
const asText = (value: unknown): string =>
  typeof value === 'string'
    ? value
    : value === null || value === undefined
      ? ''
      : JSON.stringify(value);
// A `resolveAny` answer: {type, value | address | exchange | ...}.
const answerValue = (answer: Record<string, unknown>): string =>
  asText(answer.value ?? answer.address ?? answer.exchange ?? answer.nsname ?? answer.entries);

const dnsRows = ({ domain, dnsRecords, rackspace }: DnsDiagnosis): DnsRow[] => {
  const rows: DnsRow[] = [];
  if (dnsRecords && typeof dnsRecords === 'object') {
    const r = dnsRecords as Record<string, unknown>;
    for (const ip of asList(r.root))
      rows.push({ source: 'Public DNS', type: 'A', name: domain, value: asText(ip) });
    for (const answer of asList(r.www)) {
      const a = (answer ?? {}) as Record<string, unknown>;
      rows.push({
        source: 'Public DNS',
        type: asText(a.type),
        name: `www.${domain}`,
        value: answerValue(a),
      });
    }
    for (const ns of asList(r.ns))
      rows.push({ source: 'Public DNS', type: 'NS', name: domain, value: asText(ns) });
    for (const ip of asList(r.resolve))
      rows.push({
        source: 'Public DNS',
        type: 'A (resolved)',
        name: `www.${domain}`,
        value: asText(ip),
      });
  }
  if (rackspace && typeof rackspace === 'object') {
    for (const record of Object.values(
      (rackspace as { records?: Record<string, Record<string, unknown>> }).records ?? {},
    )) {
      rows.push({
        source: 'Rackspace',
        type: asText(record.type),
        name: asText(record.name),
        value: asText(record.data),
      });
    }
  }
  return rows;
};

const DNS_COLUMNS: PanelTableColumn<DnsRow>[] = [
  { header: 'Source', render: (r) => r.source },
  { header: 'Type', render: (r) => r.type },
  { header: 'Name', render: (r) => <span className="font-mono text-xs">{r.name}</span> },
  {
    header: 'Value',
    render: (r) => <span className="break-all font-mono text-xs">{r.value}</span>,
  },
];

const DnsResult = ({ diagnosis }: { diagnosis: DnsDiagnosis }) => {
  const { domain, dnsRecords, rackspace, nameservers } = diagnosis;
  const ns =
    nameservers && typeof nameservers === 'object'
      ? (nameservers as { ok?: boolean; message?: string })
      : null;
  const zoneId =
    rackspace && typeof rackspace === 'object'
      ? asText((rackspace as { zoneId?: unknown }).zoneId)
      : '';
  const notes = [
    typeof dnsRecords === 'string' && `Public DNS lookup failed: ${dnsRecords}`,
    typeof rackspace === 'string'
      ? `Rackspace lookup failed: ${rackspace}`
      : rackspace === null || rackspace === undefined
        ? `No Rackspace zone for ${domain}.`
        : `Rackspace zone ${zoneId}.`,
    typeof nameservers === 'string' && `Nameserver check failed: ${nameservers}`,
  ].filter(Boolean) as string[];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500 dark:text-gray-400">
        {ns && (
          <Badge color={ns.ok ? 'success' : 'warning'}>
            {ns.message ??
              (ns.ok
                ? 'Nameservers point at Rackspace.'
                : 'Nameservers do not point at Rackspace.')}
          </Badge>
        )}
        {notes.map((note) => (
          <span key={note}>{note}</span>
        ))}
      </div>
      <PanelTable
        columns={DNS_COLUMNS}
        data={dnsRows(diagnosis)}
        getRowId={(r, i) => `${r.source}-${r.type}-${r.name}-${i}`}
        emptyMessage={`No DNS records found for ${domain}.`}
        emptyDescription="Check the domain spelling, then run the check again."
        ariaLabel={`DNS records for ${domain}`}
        testId="utilities-dns-records"
      />
    </div>
  );
};

export const Utilities = () => {
  const [diagnoseAll] = useLazyDiagnoseAllQuery();
  const [upsertCname] = useUpsertCnameMutation();
  const [emailSetup] = useEmailSetupMutation();
  const [disableSsl] = useDisableSslMutation();

  // The tab lives in the URL (§1.4), so ?tab=domain links straight to a tab.
  const [tab, setTab] = useUrlTab(TAB_IDS, 'dns');

  return (
    <PageShell>
      <PageHeader title="Utilities" breadcrumbs={[{ label: 'Utilities' }]} />

      <Tabs
        tabs={TABS}
        activeId={tab}
        onChange={(id) => setTab(id as (typeof TAB_IDS)[number])}
        ariaLabel="Utilities"
        idPrefix="utilities"
      />

      {/* Inactive tabs stay mounted (hidden) so typed inputs and results survive a tab switch. */}
      <section
        role="tabpanel"
        id="utilities-panel-dns"
        aria-labelledby="utilities-tab-dns"
        className={tab === 'dns' ? 'space-y-6' : 'hidden'}
      >
        <UtilityCard
          id="dns-diagnostics"
          title="DNS diagnostics"
          description="Look up a domain's DNS records, Rackspace configuration and nameservers."
          fields={[{ name: 'domain', label: 'Domain', placeholder: 'example.com' }]}
          runLabel="Check DNS"
          onRun={({ domain }) => diagnoseAll(domain.trim()).unwrap()}
          renderResult={(result) => <DnsResult diagnosis={result as DnsDiagnosis} />}
        />

        <UtilityCard
          id="upsert-cname"
          title="Upsert CNAME record"
          fields={[
            { name: 'domainName', label: 'Domain', placeholder: 'example.com' },
            { name: 'cname', label: 'CNAME (record name)', placeholder: 'www.example.com' },
            { name: 'data', label: 'Points to', placeholder: 'example_com.sites.bkat.io' },
          ]}
          runLabel="Upsert CNAME"
          onRun={({ domainName, cname, data }) =>
            upsertCname({
              domainName: domainName.trim(),
              cname: cname.trim(),
              data: data.trim(),
            }).unwrap()
          }
        />
      </section>

      <section
        role="tabpanel"
        id="utilities-panel-email"
        aria-labelledby="utilities-tab-email"
        className={tab === 'email' ? undefined : 'hidden'}
      >
        <UtilityCard
          id="email-setup"
          title="Email setup (SendGrid)"
          fields={[
            { name: 'method', label: 'Operation', options: EMAIL_METHODS },
            { name: 'domainName', label: 'Domain', placeholder: 'example.com' },
            {
              name: 'webhook',
              label: 'Webhook / to-address',
              optional: true,
              placeholder: 'only for setupInbound / testSend',
            },
          ]}
          runLabel="Run"
          onRun={({ method, domainName, webhook }) =>
            emailSetup({
              method,
              domainName: domainName.trim(),
              webhook: webhook?.trim() || undefined,
            }).unwrap()
          }
        />
      </section>

      <section
        role="tabpanel"
        id="utilities-panel-ssl"
        aria-labelledby="utilities-tab-ssl"
        className={tab === 'ssl' ? undefined : 'hidden'}
      >
        <UtilityCard
          id="disable-ssl"
          title="Disable SSL for a domain"
          danger
          confirm={{
            message: ({ domain }) => `Disable SSL for '${domain.trim()}'?`,
            dangerNote: 'HTTPS stops working for this domain until SSL is set up again.',
          }}
          fields={[{ name: 'domain', label: 'Domain', placeholder: 'example.com' }]}
          runLabel="Disable SSL"
          onRun={({ domain }) => disableSsl({ domain: domain.trim() }).unwrap()}
        />
      </section>

      <section
        role="tabpanel"
        id="utilities-panel-domain"
        aria-labelledby="utilities-tab-domain"
        className={tab === 'domain' ? undefined : 'hidden'}
      >
        <DomainMainContent />
      </section>
    </PageShell>
  );
};

export default Utilities;
